import { SpeakingLevelBadge } from "../UI/SpeakingLevelBadge.tsx";
import { useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline/index.js";
import { useCopyToClipboard } from "../UI/Feedback/useCopyToClipboard.ts";
import { SpeakingClassLevel, TopicItemFragment } from "../gql/graphql.ts";
import { DropDownItem } from "../UI/DropDown/DropDownItem.tsx";
import { DropDown } from "../UI/DropDown/DropDown.tsx";
import { BasicTable } from "../UI/Table/BasicTable.tsx";
import {
  SortDirection,
  SortLabels,
  TableColumnHeader,
} from "../UI/Table/TableColumnHeader.tsx";
import {
  CheckboxListFilter,
  CheckboxListOption,
} from "../UI/Table/Filters/CheckboxListFilter.tsx";
import { compareAlphabetically } from "../UI/Table/compareAlphabetically.ts";
import {
  PdfPresence,
  SPEAKING_CLASS_LEVELS_FROM_LOWEST,
  speakingClassLevelRank,
  TopicTableFilters,
  TopicTableSort,
  TopicTableSortField,
  userGroupNamesOf,
} from "./topicTableState.ts";

type ColumnFilters = Omit<TopicTableFilters, "search">;

const ALPHABETICAL_SORT_LABELS: SortLabels = { asc: "A → Z", desc: "Z → A" };
const LEVEL_SORT_LABELS: SortLabels = {
  asc: "Lowest first",
  desc: "Highest first",
};

const LEVEL_OPTIONS: CheckboxListOption<SpeakingClassLevel>[] = [
  ...SPEAKING_CLASS_LEVELS_FROM_LOWEST,
]
  .reverse()
  .map((level) => ({ value: level, label: level }));

const PDF_PRESENCE_OPTIONS: CheckboxListOption<PdfPresence>[] = [
  { value: "withPdf", label: "With PDF" },
  { value: "withoutPdf", label: "Without PDF" },
];

type Props = {
  topics: TopicItemFragment[];
  userGroupOptions: CheckboxListOption<string>[];
  sort: TopicTableSort | undefined;
  onSortChange: (sort: TopicTableSort) => void;
  filters: ColumnFilters;
  onFiltersChange: (filters: ColumnFilters) => void;
  editTopic: (topic: TopicItemFragment) => void;
  deleteTopic: (topic: TopicItemFragment) => void;
};

export function TopicTable(props: Props) {
  const copyToClipboard = useCopyToClipboard();
  const columnHelper = createColumnHelper<TopicItemFragment>();

  const columns = useMemo(() => {
    const sortableBy = (
      field: TopicTableSortField,
      sortLabels: SortLabels,
    ) => ({
      sortLabels,
      sortDirection:
        props.sort?.sortBy === field ? props.sort.sortDirection : undefined,
      onSort: (sortDirection: SortDirection) =>
        props.onSortChange({ sortBy: field, sortDirection }),
    });

    function filterableBy<Value extends string>(
      field: keyof ColumnFilters,
      options: CheckboxListOption<Value>[],
    ) {
      const selected = props.filters[field] as Value[];
      return {
        isFiltered: selected.length > 0,
        renderFilter: (close: () => void) => (
          <CheckboxListFilter<Value>
            options={options}
            value={selected}
            close={close}
            onApply={(nextSelected) =>
              props.onFiltersChange({
                ...props.filters,
                [field]: nextSelected,
              })
            }
          />
        ),
      };
    }

    return [
      columnHelper.accessor("name", {
        sortingFn: compareAlphabetically,
        header: () => (
          <TableColumnHeader
            label="Name"
            {...sortableBy("name", ALPHABETICAL_SORT_LABELS)}
          />
        ),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("level", {
        sortingFn: (rowA, rowB) =>
          speakingClassLevelRank(rowA.original.level) -
          speakingClassLevelRank(rowB.original.level),
        header: () => (
          <TableColumnHeader
            label="Level"
            {...sortableBy("level", LEVEL_SORT_LABELS)}
            {...filterableBy("levels", LEVEL_OPTIONS)}
          />
        ),
        cell: (info) => <SpeakingLevelBadge level={info.getValue()} />,
      }),
      columnHelper.accessor((topic) => topic.pdfUrl || undefined, {
        id: "pdfUrl",
        sortingFn: compareAlphabetically,
        sortUndefined: "last",
        meta: { className: "hidden md:table-cell" },
        header: () => (
          <TableColumnHeader
            label="Pdf Url"
            {...sortableBy("pdfUrl", ALPHABETICAL_SORT_LABELS)}
            {...filterableBy("pdfPresences", PDF_PRESENCE_OPTIONS)}
          />
        ),
        cell: (info) => (
          <div
            onClick={() => {
              copyToClipboard(info.row.original.pdfUrl);
            }}
            className={
              "max-w-[100px] overflow-hidden overflow-ellipsis cursor-pointer"
            }
          >
            {info.row.original.pdfUrl}
          </div>
        ),
      }),
      columnHelper.accessor((topic) => userGroupNamesOf(topic) || undefined, {
        id: "userGroups",
        sortingFn: compareAlphabetically,
        sortUndefined: "last",
        header: () => (
          <TableColumnHeader
            label="User groups"
            {...sortableBy("userGroups", ALPHABETICAL_SORT_LABELS)}
            {...filterableBy("userGroupIds", props.userGroupOptions)}
          />
        ),
        cell: (info) => (
          <div className={"max-w-[200px] overflow-hidden overflow-ellipsis"}>
            {userGroupNamesOf(info.row.original)}
          </div>
        ),
      }),
      columnHelper.display({
        id: "actions",
        cell: (v) => (
          <div className={"flex justify-end"}>
            <DropDown
              activator={
                <EllipsisHorizontalIcon
                  className={"text-slate-800 w-5 h-5 group-hover:text-white"}
                />
              }
            >
              <DropDownItem onClick={() => props.editTopic(v.row.original)}>
                <PencilSquareIcon className={"w-5 h-5 mr-2"} />
                Edit
              </DropDownItem>
              <DropDownItem onClick={() => props.deleteTopic(v.row.original)}>
                <TrashIcon className={"w-5 h-5 mr-2"} />
                Delete
              </DropDownItem>
            </DropDown>
          </div>
        ),
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.sort,
    props.onSortChange,
    props.filters,
    props.onFiltersChange,
    props.userGroupOptions,
    props.editTopic,
    props.deleteTopic,
    copyToClipboard,
  ]);

  const sorting = useMemo(
    () =>
      props.sort
        ? [{ id: props.sort.sortBy, desc: props.sort.sortDirection === "desc" }]
        : [],
    [props.sort],
  );

  const table = useReactTable({
    columns,
    data: props.topics,
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <BasicTable table={table} />
      {props.topics.length === 0 && (
        <p className="py-8 text-center text-sm text-slate-400">
          No topics match the current filters.
        </p>
      )}
    </>
  );
}
